import Phaser from 'phaser';
import { activePalette } from './palette';

const FONT = 'Fredoka, system-ui, -apple-system, sans-serif';
const DPR = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

export interface ButtonOptions {
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  onClick: () => void;
  /** Base fill color (primary) or accent/stroke color (secondary). */
  color?: number;
  textColor?: string;
  fontSize?: number;
  /** 'primary' = filled candy-gradient CTA; 'secondary' = dark glassy w/ accent. */
  variant?: 'primary' | 'secondary';
  /** Gentle idle breathing glow — use on the single main CTA of a screen. */
  pulse?: boolean;
  depth?: number;
}

/** Lighten a 0xRRGGBB color toward white by `amt` (0..1). */
function lighten(color: number, amt: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const lr = Math.round(r + (255 - r) * amt);
  const lg = Math.round(g + (255 - g) * amt);
  const lb = Math.round(b + (255 - b) * amt);
  return (lr << 16) | (lg << 8) | lb;
}

/**
 * A polished, reusable button: rounded corners, a soft drop shadow, a vertical
 * candy gradient (primary) or dark glassy fill (secondary), a top sheen, and a
 * springy press. Returns a Container positioned at (x, y) — move/înteract with
 * it like any GameObject. Replaces the flat sharp-cornered rectangles that read
 * as "template".
 */
export function createButton(scene: Phaser.Scene, opts: ButtonOptions): Phaser.GameObjects.Container {
  const pal = activePalette();
  const {
    x, y, width: w, height: h, label, onClick,
    color = pal.button,
    fontSize = 18,
    variant = 'primary',
    pulse = false,
    depth = 0,
  } = opts;
  // Primary CTAs keep white-on-candy; secondary buttons sit on a palette
  // surface, so their label follows the palette text (light themes would
  // otherwise render white-on-cream).
  const textColor = opts.textColor ?? (variant === 'primary' ? '#ffffff' : textCss(pal.text));

  const radius = Math.min(h / 2, 18);
  const hw = w / 2;
  const hh = h / 2;
  const container = scene.add.container(x, y).setDepth(depth);

  // Idle pulse glow (behind everything) — soft accent halo that breathes.
  if (pulse) {
    const glow = scene.add.graphics();
    glow.fillStyle(color, 0.4);
    glow.fillRoundedRect(-hw - 6, -hh - 6, w + 12, h + 12, radius + 6);
    glow.setAlpha(0.25);
    container.add(glow);
    scene.tweens.add({
      targets: glow, alpha: 0.55, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });
  }

  // Drop shadow — palette-tinted (warm clay brown on light themes, near-black
  // on dark ones) so the claymorphic soft-shadow read holds everywhere.
  const shadow = scene.add.graphics();
  shadow.fillStyle(pal.chipShadow, 0.35);
  shadow.fillRoundedRect(-hw, -hh + 4, w, h, radius);
  container.add(shadow);

  // Body.
  const body = scene.add.graphics();
  if (variant === 'primary') {
    const top = lighten(color, 0.28);
    body.fillGradientStyle(top, top, color, color, 1, 1, 1, 1);
    body.fillRoundedRect(-hw, -hh, w, h, radius);
    body.lineStyle(2, 0xffffff, 0.25);
    body.strokeRoundedRect(-hw, -hh, w, h, radius);
  } else {
    body.fillStyle(pal.surface, 0.92);
    body.fillRoundedRect(-hw, -hh, w, h, radius);
    body.lineStyle(2, color, 0.7);
    body.strokeRoundedRect(-hw, -hh, w, h, radius);
  }
  container.add(body);

  // Top sheen — a soft highlight on the upper half for a glossy candy read.
  const sheen = scene.add.graphics();
  sheen.fillStyle(0xffffff, variant === 'primary' ? 0.16 : 0.06);
  sheen.fillRoundedRect(-hw + 5, -hh + 4, w - 10, h * 0.42, radius * 0.8);
  container.add(sheen);

  // Label.
  const text = scene.add
    .text(0, 0, label, {
      fontFamily: FONT, fontSize: `${fontSize}px`, fontStyle: 'bold', color: textColor, resolution: DPR,
    })
    .setOrigin(0.5);
  container.add(text);

  // Transparent hit target sized to the button.
  const hit = scene.add.rectangle(0, 0, w, h, 0x000000, 0).setInteractive({ useHandCursor: true });
  container.add(hit);

  const press = () => scene.tweens.add({ targets: container, scale: 0.95, duration: 80, ease: 'Quad.easeOut' });
  const release = () => scene.tweens.add({ targets: container, scale: 1, duration: 160, ease: 'Back.easeOut' });

  hit.on('pointerdown', press);
  hit.on('pointerup', () => { release(); onClick(); });
  hit.on('pointerout', release);

  // Expose the label so callers can update it (e.g. SHARE -> COPIED!).
  (container as Phaser.GameObjects.Container & { label: Phaser.GameObjects.Text }).label = text;
  return container;
}

export interface IconButtonOptions {
  x: number;
  y: number;
  /** Square edge length. Default 46. */
  size?: number;
  onClick: () => void;
  /** Fires on pointerdown before the press tween (e.g. suppress a game drop). */
  onDown?: () => void;
  /** Draws the glyph centered at (0, 0); receives the accent color. */
  draw: (g: Phaser.GameObjects.Graphics, accent: number) => void;
  /** Accent color for stroke + glyph. Defaults to palette primary. */
  color?: number;
  /** Tiny all-caps caption beneath the button. */
  caption?: string;
  /** Rotate the glyph (radians) — e.g. the tilted phone icon. */
  glyphRotation?: number;
  /** Start highlighted (active/“on” look). */
  active?: boolean;
  depth?: number;
}

/** A container with the extra controls an icon button exposes to callers. */
export type IconButton = Phaser.GameObjects.Container & {
  /** Toggle the active (highlighted) look — repaints body + glyph. */
  setHighlight: (on: boolean) => void;
  /** The caption Text, if one was created. */
  caption?: Phaser.GameObjects.Text;
};

/**
 * A polished square "chip" icon button: soft shadow, glassy vertical gradient,
 * accent hairline, top sheen, a vector glyph, an optional caption, and the same
 * springy press as {@link createButton}. Active state swaps the fill to the
 * palette's toggle-on tone and brightens the accent. Used for the HUD cog/tilt
 * and the menu's settings + leaderboard buttons so all chrome reads as one set.
 */
export function createIconButton(scene: Phaser.Scene, opts: IconButtonOptions): IconButton {
  const pal = activePalette();
  const {
    x, y, size = 46, onClick, onDown, draw,
    color = pal.iconAccent ?? pal.primary, caption, glyphRotation = 0, depth = 0,
  } = opts;
  let active = opts.active ?? false;

  const radius = Math.round(size * 0.32);
  const hs = size / 2;
  const container = scene.add.container(x, y).setDepth(depth);

  // Drop shadow.
  const shadow = scene.add.graphics();
  shadow.fillStyle(pal.chipShadow, 0.5);
  shadow.fillRoundedRect(-hs, -hs + 3, size, size, radius);
  container.add(shadow);

  const body = scene.add.graphics();
  const glyph = scene.add.graphics().setRotation(glyphRotation);
  container.add(body);
  container.add(glyph);

  const paint = () => {
    body.clear();
    const top = active ? lighten(pal.toggleOn, 0.22) : lighten(pal.surface, 0.12);
    const bot = active ? pal.toggleOn : pal.surface;
    body.fillGradientStyle(top, top, bot, bot, 0.96, 0.96, 0.96, 0.96);
    body.fillRoundedRect(-hs, -hs, size, size, radius);
    body.lineStyle(1.5, color, active ? 1 : 0.5);
    body.strokeRoundedRect(-hs, -hs, size, size, radius);
    // Top sheen.
    body.fillStyle(0xffffff, 0.1);
    body.fillRoundedRect(-hs + 4, -hs + 3, size - 8, size * 0.4, radius * 0.7);
    glyph.clear();
    draw(glyph, color);
    glyph.setAlpha(active ? 1 : 0.92);
  };
  paint();

  if (caption) {
    const cap = scene.add
      .text(0, hs + 9, caption, {
        fontFamily: FONT, fontSize: '9px', fontStyle: 'bold',
        color: textCss(color), resolution: DPR,
      })
      .setOrigin(0.5, 0);
    container.add(cap);
    (container as IconButton).caption = cap;
  }

  const hit = scene.add.rectangle(0, 0, size, size, 0x000000, 0).setInteractive({ useHandCursor: true });
  container.add(hit);

  const press = () => scene.tweens.add({ targets: container, scale: 0.92, duration: 80, ease: 'Quad.easeOut' });
  const release = () => scene.tweens.add({ targets: container, scale: 1, duration: 160, ease: 'Back.easeOut' });

  hit.on('pointerdown', () => { onDown?.(); press(); });
  hit.on('pointerup', () => { release(); onClick(); });
  hit.on('pointerout', release);

  const ib = container as IconButton;
  ib.setHighlight = (on: boolean) => { active = on; paint(); };
  return ib;
}

/** 0xRRGGBB → '#rrggbb' for the caption color (avoids importing css() here). */
function textCss(color: number): string {
  return `#${(color & 0xffffff).toString(16).padStart(6, '0')}`;
}
